import React from "react";
import { Autocomplete, TextField, Typography, Checkbox } from "@mui/material";
import { MRT_Column, MRT_TableInstance, MRT_ColumnDef } from "material-react-table";

type DataRow = Record<string, any>;

const getNestedValue = (obj: any, path: string) => {
  return path.split(".").reduce((prev, curr) => {
    return prev && prev[curr] !== undefined ? prev[curr] : undefined;
  }, obj);
};

interface AutocompleteFilterProps<TData extends DataRow> {
  table: MRT_TableInstance<TData>;
  column: MRT_Column<TData>;
  accessorPath: string;
  placeholder?: string;
}

export const createAutocompleteFilterFn = <TData extends DataRow>(accessorPath: string) => {
  return (row: any, id: string, filterValue: any) => {
    if (!filterValue || filterValue.length === 0) {
      return true;
    }
    const value = getNestedValue(row.original, accessorPath);
    return filterValue.includes(value);
  };
};

export const AutocompleteFilter = <TData extends DataRow>({
  table,
  column,
  accessorPath,
  placeholder = "Filter"
}: AutocompleteFilterProps<TData>) => {
  const allValues = [
    ...new Set(
      table
        .getCoreRowModel()
        .rows.map((row) => getNestedValue(row.original, accessorPath))
        .filter(Boolean)
    )
  ];

  const selectedValues = (column.getFilterValue() as string[]) || [];

  return (
    <Autocomplete
      renderInput={(params) => <TextField {...params} placeholder={placeholder} size="small" variant="outlined" />}
      options={allValues}
      multiple
      disableCloseOnSelect
      onChange={(e, v) => column.setFilterValue(v)}
      value={selectedValues}
      limitTags={2}
      renderOption={(props, option, { selected }) => (
        <li {...props}>
          <Checkbox style={{ marginRight: 8 }} checked={selected} />
          {option}
        </li>
      )}
      sx={{
        minWidth: 200,
        maxWidth: 350,
        "& .MuiAutocomplete-tag": {
          maxWidth: 100,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap"
        }
      }}
      ChipProps={{
        size: "small",
        sx: {
          maxWidth: 100,
          overflow: "hidden",
          textOverflow: "ellipsis"
        }
      }}
    />
  );
};

export const createCellRenderer = <TData extends DataRow>(
  accessorPath: string,
  options?: {
    paddingRight?: number;
    variant?: "body1" | "body2" | "caption" | "h6" | "subtitle1" | "subtitle2";
  }
) => {
  return ({ row }: { row: any }) => (
    <Typography variant={options?.variant || "body2"} paddingRight={options?.paddingRight || 3}>
      {getNestedValue(row.original, accessorPath)}
    </Typography>
  );
};

type AutocompleteColumnOptions = {
  placeholder?: string;
  headerTooltip?: string;
  cellPaddingRight?: number;
  textVariant?: "body1" | "body2" | "caption" | "h6" | "subtitle1" | "subtitle2";
  size?: number;
};

export function createAutocompleteColumn<TData extends DataRow>(
  header: string,
  accessorPath: string,
  options?: AutocompleteColumnOptions
): MRT_ColumnDef<TData> {
  return {
    header,
    accessorKey: accessorPath,
    filterFn: createAutocompleteFilterFn<TData>(accessorPath),
    Filter: ({ table, column }: { table: MRT_TableInstance<TData>; column: MRT_Column<TData> }) => (
      <AutocompleteFilter
        table={table}
        column={column}
        accessorPath={accessorPath}
        placeholder={options?.placeholder || header}
      />
    ),
    Cell: createCellRenderer<TData>(accessorPath, {
      paddingRight: options?.cellPaddingRight,
      variant: options?.textVariant
    }),
    size: options?.size,
    muiFilterTextFieldProps: {
      variant: "outlined",
      size: "small"
    }
  };
}
